import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef, EventEmitter,
  inject,
  Input,
  OnInit, Output, QueryList, Renderer2,
  ViewChild
} from '@angular/core';
import {FlowDirective, NgxFlowModule} from '@flowjs/ngx-flow';
import {AsyncPipe, JsonPipe, NgClass, NgOptimizedImage, PercentPipe} from '@angular/common';
import {FlowChunk, FlowFile, FlowOptions} from 'flowjs';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {environment} from '../../../environments/environment';
import {SessionStorageService} from 'ngx-webstorage';
import { v4 as uuidv4 } from 'uuid';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {MatIcon} from '@angular/material/icon';
import {MatMiniFabButton} from '@angular/material/button';
import {TokenService} from '../../services/token.service';
import {catchError, switchMap, tap, throwError} from 'rxjs';
import {MatSnackBar} from '@angular/material/snack-bar';
import {FileAttachmentInterface} from '../../interfaces/http.interface';
import {UPLOAD_IMAGE_TYPES, UPLOAD_VIDEO_TYPES} from '../../utils/constants/uploads-types';
import {FormsModule} from '@angular/forms';


@Component({
  selector: 'app-file-upload-component',
  imports: [
    NgxFlowModule,
    PercentPipe,
    AsyncPipe,
    NgClass,
    MatProgressSpinner,
    MatIcon,
    MatMiniFabButton,
    FormsModule,
    JsonPipe,
    NgOptimizedImage
  ],
  templateUrl: './file-upload-component.component.html',
  styleUrl: './file-upload-component.component.scss'
})
export class FileUploadComponentComponent implements OnInit, AfterViewInit {
  @Input() public typeFile: string = 'file';
  @Input() public sendLoading: boolean = false;
  @Output() public loadingChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @ViewChild('inputFile') inputFile!: ElementRef<HTMLInputElement>;
  @ViewChild(FlowDirective) flow?: FlowDirective;
  getIndex!: () => number;
  remove!: () => void;
  public loading: boolean = false;
  public currentFileToUpload: FileAttachmentInterface | null = null;
  public uploadError: boolean = false;
  private fileInputOpened = false;
  public chunkRetries = 0;
  public accept: string = ''
  private token: string =  inject(SessionStorageService).retrieve('token')?.token
  private tokenService: TokenService =  inject(TokenService);
  private sessionStorageService: SessionStorageService =  inject(SessionStorageService);
  private snackBar: MatSnackBar =  inject(MatSnackBar);
  public uploadConfig: FlowOptions = {
    target: `${environment.cdnHttp}/cdn/v2/${this.sessionStorageService.retrieve('userType')}/upload`,
    singleFile: true,
    method: 'POST',
    testChunks: false,
    fileParameterName: 'content',
    chunkRetryInterval: 15000,
    chunkSize: 100 * 1024 * 1024,
    maxChunkRetries: 6,
    successStatuses: [200, 201, 204],
    headers: (file: any) => ({
      'Authorization': `Bearer ${this.token}`,
      'X-Mgk-Upload-File-Name': encodeURIComponent(file.name.replaceAll(' ', '_')),
      'X-Mgk-Upload-Command': 'upload, finalize',
      'X-Mgk-Upload-Entity-Id': `${uuidv4()}`,
      'X-Mgk-Upload-Type': this.typeFile,
      'X-Mgk-Upload-Entity': this.typeFile === 'image' ? `support,attachment` : `support,${this.typeFile}`
    })
  };


  private cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  private destroyRef: DestroyRef = inject(DestroyRef);

  ngOnInit(): void {
    if(this.typeFile === 'image') {
      this.accept = UPLOAD_IMAGE_TYPES;
    } else if (this.typeFile === 'video') {
      this.accept = UPLOAD_VIDEO_TYPES;
    }
    this.sessionStorageService.observe('token')
      .pipe(
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(
        (token) => {
          this.token = token?.token || '';
        }
      )
  }

  ngAfterViewInit() {
    this.flow?.events$
      .pipe(
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(event => {
        console.log(event);
        if(event.type === 'fileAdded') {
          if(!this.checkFileAdded(event.event[0] as FlowFile)) {
            this.flow?.cancel()
            this.destroyComponent();
          }
        } else if(event.type === 'filesSubmitted') {
          this.loading = true;
          return this.flow?.upload();
        } else if(event.type === 'fileError') {
            this.viewError(JSON.parse(event.event[1] as string)?.message);
            this.flow?.cancel();
            this.destroyComponent();
          this.loading = false;
        }  else if(event.type === 'fileSuccess') {
          this.chunkRetries = 0;
          if(this.typeFile === 'image') {
            const link = JSON.parse(event.event[1] as string)?.links?.find((l: string) => l.includes('_o'));
            if(link) {
              this.currentFileToUpload = {
                url: link,
                name: (event.event[0] as FlowFile).name,
                file_type: this.typeFile
              };
            }
          } else if( this.typeFile === 'video') {
            const link = JSON.parse(event.event[1] as string)?.links?.find((l: string) => !l.includes('_thumb'));
            const thumb_link = JSON.parse(event.event[1] as string)?.links?.find((l: string) => l.includes('_thumb'));
            if(link) {
              this.currentFileToUpload = {
                url: link,
                thumb_url: thumb_link,
                name: (event.event[0] as FlowFile).name,
                file_type: this.typeFile
              };
            }
          }
          this.loading = false;
        } else if (event.type === 'newFlowJsInstance') {
          this.cdr.detectChanges();
        } else if(event.type === 'fileRetry') {
          this.chunkRetries +=1;
          if(this.chunkRetries > 4) {
            this.onFileError(event.event[0] as FlowFile, event.event[1] as FlowChunk);
          }
        } else if(event.type === 'fileRemoved') {
          this.loading = false;
        }
        this.loadingChange.emit(this.loading);
    });
    this.inputFile.nativeElement.click();
  }

  onFileError(flowFile: FlowFile, chunk: FlowChunk) {

      this.tokenService.isRefreshing = true;
      this.tokenService.refreshOctobusToken(this.sessionStorageService.retrieve('token')).pipe(
        tap((newTokens) => {
          this.sessionStorageService.store('token', newTokens);
          this.tokenService.refreshTokenSubject.next(newTokens);
        }),
        tap((newTokens) => {
          this.tokenService.isRefreshing = false;
          this.token = newTokens?.token; // Оновлюємо токен
          // this.chunkRetries = 0;
          // chunk.retries = 0;
        }),
        catchError((err) => {
          // Якщо оновлення токена не вдалося, перенаправляємо на авторизацію
          if(err.status === 401) {
            return this.tokenService.getOctobusToken().pipe(
              tap((newTokens) => {
                this.sessionStorageService.store('token', newTokens);
                this.tokenService.refreshTokenSubject.next(newTokens);
                this.tokenService.isRefreshing = false;
                this.token = newTokens?.token || ''; // Оновлюємо токен
                // this.chunkRetries = 0;
                // chunk.retries = 0;
              }),
              catchError(() => {
                return throwError(() => err);
              })
            )
          } else {
            this.tokenService.isRefreshing = false;
            this.tokenService.refreshTokenSubject.next(null);
            return throwError(() => err);
          }
        }),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe();
  }


  destroyComponent() {
    this.remove();
  }

  viewError(message: string) {
    this.snackBar.open(message, undefined, {
      horizontalPosition: "center",
      verticalPosition: "top",
      duration: 3000,
      panelClass: ['error-snackbar']
    });
  }

  checkFileAdded(file: FlowFile): boolean {
    const maxSize = 100 * 1024 * 1024;

    if (file.size > maxSize) {
      this.viewError(`❌ Файл занадто великий`)
      return false; // Відхиляємо файл
    }

    return true;
  }

  onFileInputClick() {
    this.fileInputOpened = true;
    setTimeout(() => {
      window.addEventListener('focus', this.onWindowFocus);
    }, 100);
  }

  onFileChange(event: Event) {
    this.fileInputOpened = false;
    window.removeEventListener('focus', this.onWindowFocus);
  }

  onWindowFocus = () => {
    setTimeout(() => {
      if (this.fileInputOpened) {
        this.flow?.cancel();
        this.destroyComponent();
        this.fileInputOpened = false;
        window.removeEventListener('focus', this.onWindowFocus);
      }
    }, 300);
  };
}
